import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { getAdminToken } from '@/lib/admin-store'
import { getAdminNavLinks, readAdminFiltersFromSearch, type AdminFilters } from './-admin-utils'
import styles from './admin.module.css'

export const Route = createFileRoute('/admin/promoters/$promoterId')({
  component: AdminPromoterDetailRoute,
})

function AdminPromoterDetailRoute() {
  const { promoterId } = Route.useParams()
  const [token] = useState(getAdminToken)
  const [filters] = useState<AdminFilters>(readAdminFiltersFromSearch)
  const [detail, setDetail] = useState<Awaited<
    ReturnType<typeof api.adminGetPromoterDetail>
  > | null>(null)
  const [label, setLabel] = useState('')
  const [campaignName, setCampaignName] = useState('promoter-campaign')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const navLinks = useMemo(() => getAdminNavLinks(filters), [filters])
  const hasCampaignData = (detail?.performance.links ?? 0) > 0

  const loadDetail = async (): Promise<void> => {
    if (!token) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = await api.adminGetPromoterDetail(token, promoterId)
      setDetail(data)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load promoter detail.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void loadDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoterId, token])

  const handleSuspendOrResume = async (): Promise<void> => {
    if (!token || !detail) {
      return
    }

    setError(null)
    setNotice(null)
    setIsLoading(true)

    try {
      if (detail.status === 'suspended') {
        await api.adminResumePromoter(token, detail.promoterId, 'review_completed')
        setNotice('Promoter resumed.')
      } else {
        await api.adminSuspendPromoter(token, detail.promoterId, 'abuse_review_pending')
        setNotice('Promoter suspended.')
      }
      await loadDetail()
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : 'Promoter status update failed.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateCampaignLink = async (): Promise<void> => {
    if (!token) {
      return
    }

    setError(null)
    setNotice(null)
    setIsLoading(true)

    try {
      const created = await api.adminCreatePromoterCampaignLink(token, promoterId, {
        ...(label.trim().length > 0 ? { label: label.trim() } : {}),
        campaignName: campaignName.trim().length > 0 ? campaignName.trim() : 'promoter-campaign',
        utmCampaign: campaignName.trim().length > 0 ? campaignName.trim() : 'promoter-campaign',
      })
      setNotice(`Campaign link created: ${created.code}`)
      setLabel('')
      await loadDetail()
    } catch (createError) {
      setError(
        createError instanceof Error ? createError.message : 'Campaign link creation failed.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const backHref = useMemo(() => {
    const qs = typeof window !== 'undefined' ? window.location.search : ''
    return `/admin/promoters${qs}`
  }, [])

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <h1 className={styles.title}>Promoter Detail</h1>
        <p className={styles.subtitle}>
          Manage status, links, and performance for promoter <strong>{promoterId}</strong>.
        </p>
      </header>

      <section className={styles.card}>
        <div className={styles.tabs}>
          {navLinks.map((link) => (
            <a key={link.path} href={link.href} className={styles.tabLink}>
              {link.label}
            </a>
          ))}
        </div>
      </section>

      {!token && (
        <section className={styles.card}>
          <p className={styles.emptyState}>You don't have admin access.</p>
          <a className={styles.tabLink} href="/admin">
            Go to Home
          </a>
        </section>
      )}

      {token && isLoading && (
        <section className={styles.card}>
          <p className={styles.muted}>Loading promoter detail...</p>
        </section>
      )}

      {token && error && (
        <section className={styles.card}>
          <p className={styles.error}>{error}</p>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => void loadDetail()}
          >
            Retry
          </button>
        </section>
      )}

      {token && detail && !error && (
        <section className={styles.card}>
          <p className={styles.muted}>
            {detail.name} ({detail.email}) • status: {detail.status} • invite: {detail.inviteCode}
          </p>
          <p className={styles.muted}>
            Credits: {detail.credits.balance} • next refresh{' '}
            {new Date(detail.credits.nextRefreshAt).toLocaleString()}
          </p>
          <div className={styles.row}>
            <button
              className={detail.status === 'suspended' ? styles.button : styles.dangerButton}
              type="button"
              onClick={() => void handleSuspendOrResume()}
              disabled={isLoading}
            >
              {detail.status === 'suspended' ? 'Resume' : 'Suspend'}
            </button>
            <a className={styles.tabLink} href={backHref}>
              Back to Promoters
            </a>
          </div>
          <div className={styles.row}>
            <input
              className={styles.input}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Link label (optional)"
            />
            <input
              className={styles.input}
              value={campaignName}
              onChange={(event) => setCampaignName(event.target.value)}
              placeholder="Campaign name"
            />
            <button
              className={styles.secondaryButton}
              type="button"
              onClick={() => void handleCreateCampaignLink()}
              disabled={isLoading}
            >
              Create Campaign Link
            </button>
          </div>
          {notice && <p className={styles.notice}>{notice}</p>}
        </section>
      )}

      {token && detail && !error && (
        <section className={styles.card}>
          <h2>Performance</h2>
          {!hasCampaignData ? (
            <>
              <p className={styles.emptyState}>No campaign performance yet.</p>
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={() => void handleCreateCampaignLink()}
                disabled={isLoading}
              >
                Create Campaign Link
              </button>
            </>
          ) : (
            <>
              <p className={styles.muted}>
                Links: {detail.performance.links} • Signups: {detail.performance.signups} • Paid:{' '}
                {detail.performance.paidConversions} • Revenue: $
                {detail.performance.revenueInfluenced.toFixed(2)}
              </p>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>URL</th>
                    <th>Signups</th>
                    <th>Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.links.map((link) => (
                    <tr key={link.id}>
                      <td>{link.code}</td>
                      <td>{link.url}</td>
                      <td>{link.signups}</td>
                      <td>{link.paidConversions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      )}
    </section>
  )
}
